import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, X } from 'lucide-react';
import { useSuppliersStore } from '../../../store/suppliersStore';
import { ManageSuppliersPanel } from './ManageSuppliersPanel';
import { supplierBadgeClass, supplierBadgeStyle } from './theme';

interface ThemeTokens {
  text: string;
  muted: string;
  border: string;
  inputBg: string;
  cardBg: string;
  rowHover: string;
}

interface SupplierSelectProps {
  value: string;
  onChange: (value: string) => void;
  isDark: boolean;
  theme: ThemeTokens;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  hoverBg?: string;
  forceHover?: boolean;
  onHoverChange?: (hovered: boolean) => void;
}

const SupplierSelect: React.FC<SupplierSelectProps> = ({
  value,
  onChange,
  isDark,
  theme,
  placeholder = '',
  disabled = false,
  className = '',
  hoverBg,
  forceHover = false,
  onHoverChange,
}) => {
  const { suppliers, fetchSuppliers } = useSuppliersStore();
  const [open, setOpen] = useState(false);
  const [managing, setManaging] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const manageRef = useRef<HTMLDivElement>(null);

  const resolvedHoverBg = hoverBg ?? (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)');

  const setHover = (next: boolean) => {
    setHovered(next);
    onHoverChange?.(next);
  };

  useEffect(() => {
    void fetchSuppliers();
  }, [fetchSuppliers]);

  const updateMenuPos = () => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const menuMaxH = 240;
    const estimatedH = 200;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < estimatedH && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(menuMaxH, openUp ? spaceAbove - 8 : spaceBelow - 8));
    const top = openUp
      ? Math.max(8, rect.top - gap - maxHeight)
      : rect.bottom + gap;
    const width = Math.max(rect.width, 200);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    setMenuPos({ top, left, width, maxHeight });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
    window.addEventListener('scroll', updateMenuPos, true);
    window.addEventListener('resize', updateMenuPos);
    return () => {
      window.removeEventListener('scroll', updateMenuPos, true);
      window.removeEventListener('resize', updateMenuPos);
    };
  }, [open]);

  useEffect(() => {
    if (!open && !managing) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (managing) {
        if (manageRef.current && !manageRef.current.contains(target)) {
          setManaging(false);
        }
        return;
      }
      if (
        buttonRef.current?.contains(target)
        || menuRef.current?.contains(target)
      ) {
        return;
      }

      setOpen(false);
      e.preventDefault();
      e.stopPropagation();

      const swallow = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
      };
      document.addEventListener('mouseup', swallow, true);
      document.addEventListener('click', swallow, true);
      window.setTimeout(() => {
        document.removeEventListener('mouseup', swallow, true);
        document.removeEventListener('click', swallow, true);
      }, 0);
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [open, managing]);

  const options = useMemo(() => {
    const names = suppliers.map(s => s.name);
    const trimmed = value.trim();
    if (trimmed && !names.some(n => n.toLowerCase() === trimmed.toLowerCase())) {
      return [trimmed, ...names];
    }
    return names;
  }, [suppliers, value]);

  const handleSelect = (name: string) => {
    onChange(value === name ? '' : name);
    setOpen(false);
  };

  const panelBg = isDark ? '#1e2330' : '#ffffff';
  const activeColor = isDark ? '#93c5fd' : '#1d4ed8';

  const menu = open && menuPos
    ? createPortal(
        <div
          ref={menuRef}
          className="fixed z-[70] rounded-md shadow-xl overflow-hidden flex flex-col"
          style={{
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: menuPos.maxHeight,
            backgroundColor: panelBg,
            border: `1px solid ${theme.border}`,
          }}
        >
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setManaging(true);
            }}
            className="w-full text-left px-2.5 py-2 text-xs font-medium transition-colors flex items-center gap-1.5 flex-shrink-0"
            style={{
              color: activeColor,
              borderBottom: `1px solid ${theme.border}`,
              backgroundColor: isDark ? '#1a2540' : '#eff6ff',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = isDark ? '#1e2a4a' : '#dbeafe';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = isDark ? '#1a2540' : '#eff6ff';
            }}
          >
            <Pencil size={12} />
            Edit Options
          </button>

          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
            {options.length === 0 ? (
              <div className="px-2.5 py-3 text-xs text-center" style={{ color: theme.muted }}>
                No suppliers yet. Use Edit Options to add one.
              </div>
            ) : (
              options.map(name => {
                const selected = value === name;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleSelect(name)}
                    className="w-full text-left px-2.5 py-1.5 text-xs transition-colors truncate"
                    style={{
                      color: selected ? activeColor : theme.text,
                      backgroundColor: selected ? theme.rowHover : 'transparent',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = theme.rowHover;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = selected ? theme.rowHover : 'transparent';
                    }}
                  >
                    {name}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  const manageModal = managing
    ? createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
        >
          <div
            ref={manageRef}
            className="w-full max-w-md rounded-lg shadow-2xl overflow-hidden"
            style={{
              backgroundColor: theme.cardBg,
              border: `1px solid ${theme.border}`,
            }}
          >
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: `1px solid ${theme.border}` }}
            >
              <div>
                <p className="text-sm font-semibold" style={{ color: theme.text }}>
                  Edit Suppliers
                </p>
                <p className="text-xs mt-0.5" style={{ color: theme.muted }}>
                  Add, rename, or remove options for ordering.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setManaging(false)}
                className="p-1.5 rounded transition-colors"
                style={{ color: theme.muted }}
                title="Close"
              >
                <X size={16} />
              </button>
            </div>
            <ManageSuppliersPanel isDark={isDark} theme={theme} compact />
            <div
              className="flex justify-end px-4 py-2.5"
              style={{ borderTop: `1px solid ${theme.border}` }}
            >
              <button
                type="button"
                onClick={() => setManaging(false)}
                className="text-xs px-3 py-1.5 rounded transition-colors font-medium"
                style={{
                  backgroundColor: isDark ? '#2b3140' : '#e5e7eb',
                  color: theme.text,
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={`relative box-border inline-flex items-center text-left min-w-0 w-full h-full cursor-pointer transition-colors outline-none focus-visible:ring-1 focus-visible:ring-blue-500/50 disabled:opacity-50 ${className}`}
        style={{
          color: value ? theme.text : theme.muted,
          backgroundColor: hovered || forceHover || open ? resolvedHoverBg : 'transparent',
          border: 'none',
        }}
        aria-label="Select supplier"
      >
        {value ? (
          <span
            className={supplierBadgeClass}
            style={supplierBadgeStyle(value, isDark)}
            title={value}
          >
            {value}
          </span>
        ) : (
          <span className="truncate leading-none text-sm">{placeholder}</span>
        )}
      </button>
      {menu}
      {manageModal}
    </>
  );
};

export default SupplierSelect;
